// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's inference engine (frontend-u0o.3; design.md §4,
 * after SketchUp): where a point dragged on the plan lands, and why. The
 * measure tools' `snapping.ts` ranks 3D features along a pick ray; this is
 * its plan-only sibling for the editor, in world x/y with a tolerance in
 * metres (the caller converts its pixel reach), so every rule is testable
 * without a scene.
 *
 * Priority (design.md): a point (endpoint, midpoint, intersection,
 * perpendicular foot) beats a plane trace, which beats a wall's axis
 * (on-edge), which beats an axis lock (the storey's Manhattan frame, red X
 * / green Y) or a parallel / perpendicular to a nearby wall, which beats
 * the 5 mm grid. Along an axis lock a trace or a wall crossing it is taken
 * as their intersection ("På flade · på rød akse"). Pure: no React, no DOM.
 */

import type { XY } from './planWalls';

export type InferenceKind =
  | 'endpoint'
  | 'midpoint'
  | 'intersection'
  | 'perpendicular'
  | 'trace'
  | 'on-edge'
  | 'axis-x'
  | 'axis-y'
  | 'parallel'
  | 'grid'
  | 'free';

/** A segment to infer against: a wall's axis, or a detected plane's trace. */
export interface SnapSegment {
  a: XY;
  b: XY;
  source: 'wall' | 'trace';
  /** "Væg 5", "P-17" — for the ScreenTip. */
  label: string;
  /** The wall's guid, so a drag can leave its own wall out. */
  guid?: string;
  /** A trace's fit, for the tip ("2 140 pkt"). */
  points?: number;
}

export interface InferenceToggles {
  snap: boolean;
  ortho: boolean;
  trace: boolean;
  grid: boolean;
}

export interface InferenceContext {
  segments: readonly SnapSegment[];
  /** The storey's Manhattan frame: the angle of its X axis, radians from world +x. */
  axisAngle: number;
  /** Where the drag started from (the fixed end, the grip's start): axis locks, parallels and feet are taken from it. */
  origin: XY | null;
  toggles: InferenceToggles;
  /** Reach, metres (the pixel radius at the current zoom). */
  tolerance: number;
  /** Segments of these guids are ignored (the wall being dragged). */
  exclude?: ReadonlySet<string>;
  /** Alt held: no snapping at all for this move. */
  suspended?: boolean;
  /** Grid step, metres (default 5 mm). */
  gridStep?: number;
}

export interface Inference {
  point: XY;
  kind: InferenceKind;
  /** The ScreenTip: "Endepunkt · Væg 5", "På flade · Væg 5 · på rød akse". */
  tip: string;
  /** A line to draw while it holds (the axis from the origin, the trace). */
  guide?: [XY, XY];
  /** The segment it holds to, if any. */
  segment?: SnapSegment;
  /** Also on the red / green axis from the origin (an axis lock met a segment). */
  axis?: 'x' | 'y';
}

/** Glyph colour token per kind (design.md §4: SketchUp's convention). */
export const INFERENCE_TOKEN: Record<InferenceKind, string> = {
  endpoint: '--snap-endpoint',
  midpoint: '--snap-midpoint',
  intersection: '--snap-intersection',
  perpendicular: '--snap-parallel',
  trace: '--snap-trace',
  'on-edge': '--snap-on-edge',
  'axis-x': '--snap-axis-x',
  'axis-y': '--snap-axis-y',
  parallel: '--snap-parallel',
  grid: '--dim-temp',
  free: '--dim-temp',
};

const DEG = Math.PI / 180;
/** An axis or a parallel holds within this angle of the drag direction. */
const ANGLE_TOL = 3 * DEG;

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const len = (a: XY) => Math.hypot(a[0], a[1]);
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** The point on segment ab nearest p, and the parameter along it (0..1). */
export function nearestOnSegment(a: XY, b: XY, p: XY): { point: XY; t: number } {
  const d = sub(b, a);
  const L2 = dot(d, d);
  const t = L2 > 0 ? Math.max(0, Math.min(1, dot(sub(p, a), d) / L2)) : 0;
  return { point: add(a, mul(d, t)), t };
}

/** Where lines (p, p + r) and (q, q + s) meet, or null when parallel. */
export function lineIntersection(p: XY, r: XY, q: XY, s: XY): XY | null {
  const den = crs(r, s);
  if (Math.abs(den) < 1e-12) return null;
  const t = crs(sub(q, p), s) / den;
  return add(p, mul(r, t));
}

/** Where two segments cross (ends included), or null. */
function segmentCross(s1: SnapSegment, s2: SnapSegment): XY | null {
  const r = sub(s1.b, s1.a);
  const s = sub(s2.b, s2.a);
  const den = crs(r, s);
  if (Math.abs(den) < 1e-12) return null;
  const qp = sub(s2.a, s1.a);
  const t = crs(qp, s) / den;
  const u = crs(qp, r) / den;
  const e = 1e-9;
  return t >= -e && t <= 1 + e && u >= -e && u <= 1 + e ? add(s1.a, mul(r, t)) : null;
}

const fmt = (v: number) => v.toFixed(2).replace('.', ',').replace('-', '−');

function traceTip(s: SnapSegment): string {
  const d = sub(s.b, s.a);
  // A trace along x says its y, along y its x (the coordinate that places it).
  const alongX = Math.abs(d[0]) >= Math.abs(d[1]);
  const at = alongX ? `y ${fmt((s.a[1] + s.b[1]) / 2)}` : `x ${fmt((s.a[0] + s.b[0]) / 2)}`;
  const pts = s.points !== undefined ? ` · ${s.points.toLocaleString('da-DK')} pkt` : '';
  return `Planspor ${s.label} · ${at}${pts}`;
}

/** The axis directions of the frame: X, Y (unit vectors). */
export function axes(angle: number): { x: XY; y: XY } {
  return { x: [Math.cos(angle), Math.sin(angle)], y: [-Math.sin(angle), Math.cos(angle)] };
}

/**
 * The storey's Manhattan angle from `GET /model/snap`'s `axis_angles`
 * (degrees), else the length-weighted wall direction modulo 90°, else 0.
 */
export function manhattanAngle(axisAngles: readonly number[] | null | undefined, segments: readonly SnapSegment[] = []): number {
  if (axisAngles && axisAngles.length > 0) return (((axisAngles[0] % 90) + 90) % 90) * DEG;
  let sx = 0;
  let sy = 0;
  for (const s of segments) {
    if (s.source !== 'wall') continue;
    const d = sub(s.b, s.a);
    const L = len(d);
    if (!(L > 0)) continue;
    // Angles ×4 fold every Manhattan direction onto one.
    const a = Math.atan2(d[1], d[0]) * 4;
    sx += Math.cos(a) * L;
    sy += Math.sin(a) * L;
  }
  if (sx === 0 && sy === 0) return 0;
  const a = Math.atan2(sy, sx) / 4;
  return ((a % (Math.PI / 2)) + Math.PI / 2) % (Math.PI / 2);
}

/**
 * Where point `p` lands. Point snaps within `tolerance`, then the traces
 * and wall axes (an axis lock from the origin crossing one is taken as the
 * crossing), then the axis lock alone, a parallel / perpendicular to a
 * nearby wall, the grid, and finally `p` itself.
 */
export function infer(p: XY, ctx: InferenceContext): Inference {
  const free: Inference = { point: p, kind: 'free', tip: '' };
  if (ctx.suspended || !ctx.toggles.snap) return free;
  const tol = ctx.tolerance;
  const segs = ctx.segments.filter((s) => !(s.guid && ctx.exclude?.has(s.guid)) && (s.source === 'wall' || ctx.toggles.trace));
  const walls = segs.filter((s) => s.source === 'wall');

  // --- points: endpoints, midpoints, intersections, perpendicular feet --
  let best: { inf: Inference; d: number; rank: number } | null = null;
  const consider = (inf: Inference, rank: number) => {
    const d = dist(inf.point, p);
    if (d > tol) return;
    if (!best || rank < best.rank || (rank === best.rank && d < best.d)) best = { inf, d, rank };
  };
  for (const s of walls) {
    consider({ point: s.a, kind: 'endpoint', tip: `Endepunkt · ${s.label}`, segment: s }, 0);
    consider({ point: s.b, kind: 'endpoint', tip: `Endepunkt · ${s.label}`, segment: s }, 0);
    consider({ point: mul(add(s.a, s.b), 0.5), kind: 'midpoint', tip: `Midtpunkt · ${s.label}`, segment: s }, 1);
  }
  // Only segments within reach can meet near p.
  const near = segs.filter((s) => dist(nearestOnSegment(s.a, s.b, p).point, p) <= tol);
  for (let i = 0; i < near.length; i += 1) {
    for (let j = i + 1; j < near.length; j += 1) {
      const x = segmentCross(near[i], near[j]);
      if (x) consider({ point: x, kind: 'intersection', tip: `Skæring · ${near[i].label} og ${near[j].label}` }, 2);
    }
  }
  if (ctx.origin) {
    for (const s of near) {
      const d = sub(s.b, s.a);
      const foot = lineIntersection(ctx.origin, [-d[1], d[0]], s.a, d);
      if (foot && nearestOnSegment(s.a, s.b, foot).t > 0 && nearestOnSegment(s.a, s.b, foot).t < 1) {
        consider({ point: foot, kind: 'perpendicular', tip: `Vinkelret på ${s.label}`, segment: s }, 3);
      }
    }
  }
  if (best) return (best as { inf: Inference }).inf;

  // --- the axis lock from the origin, if any -----------------------------
  const ax = axes(ctx.axisAngle);
  let lock: { dir: XY; axis: 'x' | 'y' } | null = null;
  if (ctx.origin && ctx.toggles.ortho) {
    const v = sub(p, ctx.origin);
    const L = len(v);
    if (L > 1e-9) {
      for (const [dir, axis] of [
        [ax.x, 'x'],
        [ax.y, 'y'],
      ] as const) {
        const off = Math.abs(crs(dir, v));
        // Within the angle, or within reach of the axis line near the origin.
        if (Math.asin(Math.min(1, off / L)) <= ANGLE_TOL || off <= tol) {
          if (!lock || off < Math.abs(crs(lock.dir, v))) lock = { dir, axis };
        }
      }
    }
  }

  // --- lines: traces before wall axes ----------------------------------
  const lineHits = near
    .map((s) => ({ s, at: nearestOnSegment(s.a, s.b, p).point }))
    .sort((a, b) => (a.s.source === b.s.source ? dist(a.at, p) - dist(b.at, p) : a.s.source === 'trace' ? -1 : 1));
  if (lineHits.length > 0) {
    const { s, at } = lineHits[0];
    const base: Inference =
      s.source === 'trace'
        ? { point: at, kind: 'trace', tip: traceTip(s), guide: [s.a, s.b], segment: s }
        : { point: at, kind: 'on-edge', tip: `På flade · ${s.label}`, segment: s };
    if (lock && ctx.origin) {
      const x = lineIntersection(ctx.origin, lock.dir, s.a, sub(s.b, s.a));
      if (x && dist(x, p) <= tol * 2) {
        return { ...base, point: x, tip: `${base.tip} · på ${lock.axis === 'x' ? 'rød' : 'grøn'} akse`, axis: lock.axis };
      }
    }
    return base;
  }

  if (lock && ctx.origin) {
    const o = ctx.origin;
    const t = dot(sub(p, o), lock.dir);
    const point = add(o, mul(lock.dir, t));
    return {
      point: ctx.toggles.grid ? alongGrid(o, lock.dir, t, ctx.gridStep) : point,
      kind: lock.axis === 'x' ? 'axis-x' : 'axis-y',
      tip: lock.axis === 'x' ? 'På rød akse' : 'På grøn akse',
      guide: [o, add(o, mul(lock.dir, t))],
    };
  }

  // --- parallel / perpendicular to a nearby wall, from the origin --------
  if (ctx.origin) {
    const v = sub(p, ctx.origin);
    const L = len(v);
    if (L > 1e-9) {
      // Walls in view: within a few metres of the pointer or the origin.
      const reach = Math.max(tol * 30, 3);
      for (const s of walls) {
        if (dist(nearestOnSegment(s.a, s.b, p).point, p) > reach && dist(nearestOnSegment(s.a, s.b, ctx.origin).point, ctx.origin) > reach) continue;
        const d = sub(s.b, s.a);
        const dl = len(d);
        if (!(dl > 0)) continue;
        const u: XY = mul(d, 1 / dl);
        for (const [dir, kind] of [
          [u, 'parallel'],
          [[-u[1], u[0]] as XY, 'perpendicular'],
        ] as const) {
          if (Math.asin(Math.min(1, Math.abs(crs(dir, v)) / L)) <= ANGLE_TOL) {
            const t = dot(v, dir);
            const point = add(ctx.origin, mul(dir, t));
            return {
              point,
              kind,
              tip: kind === 'parallel' ? `Parallel med ${s.label}` : `Vinkelret på ${s.label}`,
              guide: [ctx.origin, point],
              segment: s,
            };
          }
        }
      }
    }
  }

  if (ctx.toggles.grid) {
    const step = ctx.gridStep ?? 0.005;
    return { point: [Math.round(p[0] / step) * step, Math.round(p[1] / step) * step], kind: 'grid', tip: 'Gitter' };
  }
  return free;
}

function alongGrid(o: XY, dir: XY, t: number, step = 0.005): XY {
  return add(o, mul(dir, Math.round(t / step) * step));
}

/**
 * A wall moved along its normal (the move grip) or its face dragged (the
 * thickness grip) snaps by distance, not by point: to the line of any
 * parallel plane trace or wall within `tolerance` of the dragged offset —
 * a trace before a wall, then the nearest. `base` is a point on the moving
 * line at offset 0, `normal` its unit normal, `raw` the pointer's offset;
 * `abs` compares magnitudes (a face either side of the axis). Returns the
 * snapped offset and the inference that names it, or the raw offset alone.
 */
export function snapOffset(
  base: XY,
  normal: XY,
  raw: number,
  ctx: Pick<InferenceContext, 'segments' | 'toggles' | 'tolerance' | 'exclude' | 'suspended'>,
  abs = false,
): { offset: number; inference: Inference | null } {
  if (ctx.suspended || !ctx.toggles.snap) return { offset: raw, inference: null };
  const dir: XY = [normal[1], -normal[0]];
  let best: { offset: number; s: SnapSegment; d: number; rank: number } | null = null;
  for (const s of ctx.segments) {
    if (s.guid && ctx.exclude?.has(s.guid)) continue;
    if (s.source === 'trace' && !ctx.toggles.trace) continue;
    const d = sub(s.b, s.a);
    const L = len(d);
    if (!(L > 1e-9) || Math.abs(crs(dir, d)) / L > Math.sin(ANGLE_TOL)) continue;
    let off = dot(sub(s.a, base), normal);
    if (abs) off = Math.abs(off) * Math.sign(raw || 1);
    const gap = Math.abs(off - raw);
    if (gap > ctx.tolerance) continue;
    const rank = s.source === 'trace' ? 0 : 1;
    if (!best || rank < best.rank || (rank === best.rank && gap < best.d)) best = { offset: off, s, d: gap, rank };
  }
  if (!best) return { offset: raw, inference: null };
  const { s, offset } = best;
  const point = add(base, mul(normal, offset));
  const inference: Inference =
    s.source === 'trace'
      ? { point, kind: 'trace', tip: traceTip(s), guide: [s.a, s.b], segment: s }
      : { point, kind: 'parallel', tip: `På linje med ${s.label}`, guide: [s.a, s.b], segment: s };
  return { offset, inference };
}
